import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { own } from "@/lib/terminology";
import {
  Building2, ChevronDown, ChevronRight, LogOut, Laptop2, UserCog, PanelLeftClose,
  PanelLeftOpen, MoreHorizontal, Plus,
} from "lucide-react";
import { NAV, AUTH } from "@/constants/testIds";
import { toast } from "sonner";
import CommandBar from "@/components/app/CommandBar";
import OneCommand from "@/components/app/OneCommand";
import MyWorkChip from "@/components/app/MyWorkChip";
import HelpMenu from "@/components/app/HelpMenu";
import LanguageRegionPicker from "@/components/app/LanguageRegionPicker";
import { ChatDrawerButton } from "@/components/app/ChatDrawer";
import PreviewBanner from "@/components/app/PreviewBanner";
import { canSee } from "@/constants/roleNav";
import { isCustomerRole } from "@/constants/customerNav";
import {
  PRIMARY_SUNTRONIX, PRIMARY_CUSTOMER,
  SUB_NAV_SUNTRONIX, SUB_NAV_CUSTOMER,
  MORE_SUNTRONIX, MORE_CUSTOMER,
  primaryIdForPath,
} from "@/constants/primaryNav";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const COLLAPSE_KEY = "st.nav.collapsed";

/* ============================================================
 *  AppShell — fixed navigation, independent content scroll.
 *  See /app/memory/CHANGELOG.md for the Nav Simplification spec.
 * ============================================================ */
export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [masters, setMasters] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return window.localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    if (!user) return;
    api.get("/master-companies").then(({ data }) => {
      setMasters(data.items || []);
      if (user.master_company_id) setSelectedTenantId(user.master_company_id);
      else if (data.items?.[0]) setSelectedTenantId(null);
    }).catch(() => {});
  }, [user]);

  const isCustomer = user && isCustomerRole(user.role);
  const primary = isCustomer ? PRIMARY_CUSTOMER : PRIMARY_SUNTRONIX;
  const subNav = isCustomer ? SUB_NAV_CUSTOMER : SUB_NAV_SUNTRONIX;
  const moreGroups = isCustomer ? MORE_CUSTOMER : MORE_SUNTRONIX;

  const activePrimary = primaryIdForPath(location.pathname, isCustomer);

  const tenantName = useMemo(() => {
    if (!selectedTenantId) return "Suntronix (All Tenants)";
    return masters.find((m) => m.id === selectedTenantId)?.name || "…";
  }, [masters, selectedTenantId]);

  const activeTenant = useMemo(
    () => masters.find((m) => m.id === selectedTenantId) || null,
    [masters, selectedTenantId]
  );
  const isDemoTenant = !!activeTenant?.is_demo;

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { window.localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/login");
  };

  // Sub-nav for the active primary, filtered by role.  MORE is filtered separately below.
  const activeSubItems = useMemo(() => {
    const raw = subNav[activePrimary] || [];
    if (!user) return raw;
    if (isCustomer) return raw;   // customer roles are already scoped by CUSTOMER_NAV blueprint
    return raw.filter((it) => canSee(user.role, it.to));
  }, [activePrimary, subNav, user, isCustomer]);

  const filteredMore = useMemo(() => {
    if (!user) return [];
    return moreGroups
      .map((g) => ({ ...g, items: isCustomer ? g.items : g.items.filter((it) => canSee(user.role, it.to)) }))
      .filter((g) => g.items.length > 0);
  }, [moreGroups, user, isCustomer]);

  return (
    <div className="h-screen overflow-hidden flex bg-[color:var(--st-bg)]">
      <a href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-3 focus:py-2 focus:rounded-md focus:bg-slate-900 focus:text-white focus:text-sm"
        data-testid="skip-nav">
        Skip to main content
      </a>

      {/* ---------------------------------------------------------------- */}
      {/*  SIDEBAR — fixed height, sub-nav is the ONLY internally-scrollable */}
      {/*  region and only appears when the active section has sub-items.   */}
      {/* ---------------------------------------------------------------- */}
      <aside
        data-testid={NAV.sidebar}
        aria-label="Primary navigation"
        className={
          "h-screen border-r border-[color:var(--st-border)] bg-[color:var(--st-surface)] flex flex-col shrink-0 transition-[width] duration-150 " +
          (collapsed ? "w-[68px]" : "w-[220px]")
        }
      >
        {/* Brand — logo image contains the "suntronix" wordmark; nothing more shown alongside per brand directive */}
        <div className="h-14 px-3 border-b border-[color:var(--st-border)] flex items-center gap-2">
          <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="nav-brand">
            <img src="/suntronix-logo.webp?v=color1" alt="Suntronix"
                    className={"object-contain shrink-0 " + (collapsed ? "h-8 w-auto" : "h-10 w-auto")}
                    draggable={false} />
          </Link>
        </div>

        {/* Primary + sub-nav.  min-h-0 so the inner overflow-y-auto works. */}
        <div className="flex-1 min-h-0 flex flex-col py-2">
          <nav aria-label="Primary destinations" className="px-2 space-y-0.5 shrink-0" data-testid="primary-nav">
            {primary.map((p) => (
              <PrimaryItem
                key={p.id}
                item={p}
                collapsed={collapsed}
                active={activePrimary === p.id}
                moreGroups={filteredMore}
                onGo={(to) => navigate(to)}
              />
            ))}
          </nav>

          {/* Active section sub-nav — appears inline, ONE at a time. */}
          {!collapsed && activePrimary !== "more" && activeSubItems.length > 0 && (
            <div className="mt-4 mx-2 pt-3 border-t border-[color:var(--st-border)] flex-1 min-h-0 overflow-y-auto pr-1"
              data-testid="secondary-nav">
              <div className="px-2 pb-1 text-[10px] font-semibold tracking-[0.14em] uppercase text-[color:var(--st-text-subtle)]">
                {primary.find((p) => p.id === activePrimary)?.label}
              </div>
              <div className="space-y-0.5">
                {activeSubItems.map((it) => <SubNavLink key={it.to} {...it} />)}
              </div>
            </div>
          )}
        </div>

        {/* Collapse toggle + user tile — never scrolls. */}
        <div className="border-t border-[color:var(--st-border)] p-2">
          <button
            onClick={toggleCollapsed}
            data-testid="nav-collapse-toggle"
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            className="w-full h-8 rounded-md text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)] flex items-center justify-center gap-2 text-[11px]"
          >
            {collapsed
              ? <PanelLeftOpen className="h-4 w-4" strokeWidth={1.5} />
              : <><PanelLeftClose className="h-4 w-4" strokeWidth={1.5} /><span>Collapse</span></>}
          </button>
          <div className="mt-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="w-full flex items-center gap-2 hover:bg-[color:var(--st-surface-alt)] rounded-md p-1.5"
                  data-testid={NAV.profileMenu}
                >
                  <div className="h-7 w-7 rounded-full bg-[color:var(--st-surface-alt)] grid place-items-center text-xs font-medium text-[color:var(--st-text)] shrink-0">
                    {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
                  </div>
                  {!collapsed && (
                    <>
                      <div className="min-w-0 text-left flex-1">
                        <div className="text-[12px] font-medium text-[color:var(--st-text)] truncate">{user?.name || user?.email}</div>
                        <div className="text-[10px] text-[color:var(--st-text-muted)] truncate capitalize">{(user?.role || "").replace(/_/g, " ")}</div>
                      </div>
                      <ChevronDown className="h-3.5 w-3.5 text-[color:var(--st-text-muted)]" />
                    </>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/my-devices")}><Laptop2 className="h-4 w-4 mr-2" /> {own(user, "devices")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/users")}><UserCog className="h-4 w-4 mr-2" /> Users & Roles</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid={AUTH.logoutBtn} onClick={handleLogout} className="text-rose-600 focus:text-rose-700">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* ---------------------------------------------------------------- */}
      {/*  CONTENT COLUMN — the ONLY scrolling region for page content.     */}
      {/* ---------------------------------------------------------------- */}
      <div className="flex-1 min-w-0 h-screen flex flex-col">
        {isDemoTenant && (
          <div data-testid="demo-tenant-banner"
            className="bg-amber-100 border-b border-amber-300 text-amber-900 text-[11px] font-medium tracking-wide px-6 py-1.5 flex items-center gap-2 shrink-0">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-600 animate-pulse" />
            <span className="uppercase tracking-[0.14em]">Demo Environment · Fictional Data</span>
            <span className="text-amber-800/70 normal-case tracking-normal">— {activeTenant?.name}. Nothing here is real customer or operational data.</span>
          </div>
        )}
        <header className="h-14 border-b border-[color:var(--st-border)] bg-[color:var(--st-surface)] flex items-center px-6 gap-4 shrink-0">
          {isCustomerRole(user?.role)
            ? <CommandBar testId={NAV.searchInput} />
            : <OneCommand testId={NAV.searchInput} />}
          <div className="ml-auto flex items-center gap-1">
            {!isCustomerRole(user?.role) && (
              <button
                onClick={() => navigate("/commercial/crm/quotations?new=1")}
                data-testid="global-quick-create-quote"
                title="Quick create — new quotation"
                className="h-8 px-3 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-[12px] font-medium flex items-center gap-1.5 shadow-sm"
              >
                <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                New Quote
              </button>
            )}
            <MyWorkChip />
            <ChatDrawerButton />
            <LanguageRegionPicker />
            <HelpMenu />
            <TenantSwitcher user={user} masters={masters} selectedTenantId={selectedTenantId} setSelectedTenantId={setSelectedTenantId} tenantName={tenantName} />
          </div>
        </header>

        <div
          id="main-content"
          tabIndex={-1}
          role="main"
          data-scroll-container="true"
          className="flex-1 min-h-0 overflow-y-auto px-4 md:px-5 py-4"
        >
          <Outlet />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Primary item — either a link (Home / Work / …) or a MORE popover. */
/* ------------------------------------------------------------------ */
function PrimaryItem({ item, active, collapsed, moreGroups, onGo }) {
  const commonCls = [
    "group flex items-center gap-2.5 h-9 px-2.5 rounded-md text-[13px] font-medium transition-colors w-full",
    active
      ? "bg-[color:var(--st-text)] text-white"
      : "text-[color:var(--st-text-muted)] hover:bg-[color:var(--st-surface-alt)] hover:text-[color:var(--st-text)]",
    collapsed ? "justify-center" : "",
  ].join(" ");

  const Icon = item.icon;

  if (item.id === "more") {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            data-testid={item.testId}
            aria-label={item.label}
            className={commonCls}
            title={collapsed ? item.label : undefined}
          >
            <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
            {!collapsed && <span className="truncate flex-1 text-left">{item.label}</span>}
            {!collapsed && <MoreHorizontal className="h-3.5 w-3.5 opacity-60" />}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" side="right" sideOffset={8}
          className="w-[540px] p-0 border-[color:var(--st-border)]"
          data-testid="more-popover">
          <div className="grid grid-cols-2 gap-x-2 gap-y-1 p-4 max-h-[70vh] overflow-y-auto">
            {moreGroups.map((g) => (
              <div key={g.section} className="py-1">
                <div className="px-2 pb-1 text-[10px] font-semibold tracking-[0.14em] uppercase text-[color:var(--st-text-subtle)]">{g.section}</div>
                <div className="space-y-0.5">
                  {g.items.map((it) => (
                    <button key={it.to} onClick={() => onGo(it.to)} data-testid={it.testId}
                      className="w-full text-left px-2 py-1.5 rounded-md text-[13px] text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)]">
                      {it.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      data-testid={item.testId}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        [
          "group flex items-center gap-2.5 h-9 px-2.5 rounded-md text-[13px] font-medium transition-colors",
          (active || isActive)
            ? "bg-[color:var(--st-text)] text-white"
            : "text-[color:var(--st-text-muted)] hover:bg-[color:var(--st-surface-alt)] hover:text-[color:var(--st-text)]",
          collapsed ? "justify-center" : "",
        ].join(" ")
      }
    >
      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
      {!collapsed && <span className="truncate">{item.label}</span>}
      {!collapsed && active && <ChevronRight className="ml-auto h-3.5 w-3.5 opacity-70" />}
    </NavLink>
  );
}

/* Sub-nav link — subtle indented row inside the active primary. */
function SubNavLink({ to, label, testId, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      className={({ isActive }) =>
        [
          "flex items-center gap-2 pl-3 pr-2 h-7 rounded-md text-[12.5px] transition-colors",
          "border-l-2",
          isActive
            ? "border-[color:var(--st-accent)] text-[color:var(--st-text)] font-medium bg-[color:var(--st-surface-alt)]"
            : "border-transparent text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)]",
        ].join(" ")
      }
    >
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

function TenantSwitcher({ user, masters, selectedTenantId, setSelectedTenantId, tenantName }) {
  const isSuntronix = !user?.master_company_id;
  if (!isSuntronix) {
    return (
      <div className="text-xs text-slate-500 hidden md:flex items-center gap-1.5">
        <Building2 className="h-3.5 w-3.5 text-slate-400" strokeWidth={1.5} />
        <span>Tenant</span>
        <span className="font-medium text-slate-800">{tenantName}</span>
      </div>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="tenant-switcher"
          className="hidden md:flex items-center gap-2 h-9 px-3 rounded-md border border-slate-200 bg-white hover:border-slate-300 text-xs text-slate-700"
        >
          <Building2 className="h-3.5 w-3.5 text-slate-500" strokeWidth={1.5} />
          <span className="font-medium">{tenantName}</span>
          <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 max-h-96 overflow-y-auto">
        <DropdownMenuLabel className="text-xs">Switch tenant view</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => setSelectedTenantId(null)}>
          <span className="text-sm">Suntronix (All Tenants)</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {masters.map((m) => (
          <DropdownMenuItem key={m.id} onClick={() => setSelectedTenantId(m.id)}>
            <div className="flex-1">
              <div className="text-sm flex items-center gap-1.5">
                {m.name}
                {m.is_demo && <span className="inline-block text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold tracking-wider">DEMO</span>}
              </div>
              <div className="text-[10px] text-slate-500 font-mono">{m.code} · {m.country}</div>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
